/**
 * <s-product-gallery> — PDP main image + thumbnails.
 *
 * One horizontal scroll-snap track holds every product photo (native swipe on touch).
 * Thumbnails scroll the track to their photo; scrolling updates the active thumbnail.
 * When a variant option input with data-variant-id changes inside the product root,
 * the gallery jumps to that variant's assigned photo from the JSON map — existing
 * variant image assignments only, and only when the assigned photo actually changes.
 *
 * Required markup:
 *   [data-gallery-track]               — scroll-snap container
 *   [data-gallery-slide][data-media-id] — one per media, full track width
 *   [data-gallery-thumb]               — optional, one per slide, same order
 *   script[data-variant-media]         — JSON { variantId: mediaId }
 *   closest [data-product-root]        — scope for option change events
 */
class SProductGallery extends HTMLElement {
  #ac;
  #track;
  #slides = [];
  #thumbs = [];
  #variantMedia = {};
  #currentMediaId = '';
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;
    this.#slides = [...this.#track.querySelectorAll('[data-gallery-slide]')];
    this.#thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];

    try {
      this.#variantMedia = JSON.parse(this.querySelector('script[data-variant-media]')?.textContent || '{}');
    } catch {
      this.#variantMedia = {};
    }

    this.#thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.goTo(index), opts);
    });

    this.#track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#syncFromScroll());
    }, opts);

    const initialIndex = Number.parseInt(this.dataset.initialIndex || '0', 10) || 0;
    this.#currentMediaId = this.#slides[initialIndex]?.dataset.mediaId || '';
    if (initialIndex > 0) this.goTo(initialIndex, false);

    const root = this.closest('[data-product-root]');
    root?.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const variantId = target.dataset.variantId
        || (target instanceof HTMLSelectElement ? target.selectedOptions[0]?.dataset.variantId : '');
      if (!variantId) return;
      const mediaId = this.#variantMedia[variantId];
      if (!mediaId || String(mediaId) === this.#currentMediaId) return;
      const index = this.#slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index > -1) {
        this.#currentMediaId = String(mediaId);
        this.goTo(index);
      }
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  goTo(index, smooth = true) {
    const slide = this.#slides[index];
    if (!slide || !this.#track) return;
    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({
      // The track is position: relative, so offsetLeft is measured from the track itself.
      left: slide.offsetLeft,
      behavior: smooth && !reduceMotion ? 'smooth' : 'auto',
    });
    this.#setActive(index);
  }

  #syncFromScroll() {
    const width = this.#track.clientWidth || 1;
    const index = Math.round(Math.abs(this.#track.scrollLeft) / width);
    this.#setActive(index);
  }

  #setActive(index) {
    this.#thumbs.forEach((thumb, i) => {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
        const strip = thumb.parentElement;
        // The strip is positioned, so offsets are measured from the strip itself.
        // It scrolls horizontally on phones and vertically beside the main image on desktop.
        if (strip && strip.scrollWidth > strip.clientWidth) {
          const left = thumb.offsetLeft - (strip.clientWidth - thumb.clientWidth) / 2;
          strip.scrollTo({ left, behavior: 'auto' });
        } else if (strip && strip.scrollHeight > strip.clientHeight) {
          const top = thumb.offsetTop - (strip.clientHeight - thumb.clientHeight) / 2;
          strip.scrollTo({ top, behavior: 'auto' });
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
